import {Injectable} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import * as Sentry from '@sentry/angular';
import {BrowserOptions} from '@sentry/angular';
import {Event, EventHint} from '@sentry/types';
import {IziviaBusinessError} from '../../domain/error/iziviaBusinessError';
import {IziviaErrorReportingOptions} from '../../domain/utils/errorReporting/iziviaErrorReportingOptions';
import {isEmpty, isString} from 'lodash-es';
import {IziviaError} from '../../domain/error/iziviaError';
import {isCustomEnvironment, IziviaCustomEnvironment} from '../../domain/utils/common/serverEnvironment';

@Injectable({
    providedIn: 'root'
})
export class IziviaErrorReportingService {

    constructor() {
    }

    startReporting(options: IziviaErrorReportingOptions): void {
        const sentryOptions: BrowserOptions = {
            dsn: options.token,
            environment: isCustomEnvironment(options.environment) ? undefined : options.environment,
            denyUrls: [],
            release: `${options.project}@${options.version}`,
            normalizeDepth: 5,
            beforeSend: (event: Event, hint?: EventHint) => {
                if (this.shouldReportEvent(hint)) {
                    if (this.isNonCapturable(event)) {
                        // A non-capturable event does not keep the original exception, only its keys, so we need
                        // to add it back as an extra property
                        event.extra['originalException'] = hint.originalException instanceof Error
                            ? hint.originalException.message
                            : hint.originalException;
                    }

                    return event;
                }

                // Do not report the error to Sentry
                return null;
            }
        };
        // fix Circular dependency
        // eslint-disable-next-line no-console
        console.info(`--- [IziviaErrorReportingService] Starting Sentry with release name '${options.version}'... ---`, sentryOptions);
        Sentry.init(sentryOptions);

        this.setThirdPartyId(options.thirdPartyId);

        if (isCustomEnvironment(options.environment)) {
            this.setServerEnvironment(options.environment);
        }
    }

    capture(error: any): void {
        Sentry.captureException(error.originalError || error);
    }

    setThirdPartyId(thirdPartyId: string): void {
        Sentry.setTag('third_party_id', thirdPartyId);
    }

    setDevice(deviceId: string): void {
        Sentry.setTag('device_id', deviceId);
    }

    private setServerEnvironment(environmentConfig: IziviaCustomEnvironment): void {
        Sentry.setTags({
            'server_key': environmentConfig.serverKey,
            'server_url': environmentConfig.serverUrl
        });
    }

    /**
     need to be removed : SMBP-15809
     **/
    private shouldReportEvent(hint: EventHint): boolean {
        if (hint && hint.originalException) {
            if(hint.originalException instanceof IziviaError && hint.originalException.ignoreErrorReporting) {
                console.warn('[Sentry.beforeSend] Dropping event. Silenced IziviaError:',
                    hint.originalException.message);
                return false;
            }

            if (hint.originalException instanceof HttpErrorResponse) {
                // fix Circular dependency
                // eslint-disable-next-line no-console
                console.debug('[Sentry.beforeSend] Dropping event. HttpErrorResponse will not be reported:',
                    hint.originalException.message);
                return false;
            }
            if (hint.originalException instanceof IziviaBusinessError) {
                // fix Circular dependency
                // eslint-disable-next-line no-console
                console.debug('[Sentry.beforeSend] Dropping event. IziviaBusinessError will not be reported:',
                    hint.originalException.message);
                return false;
            }

            // SAM-1074: Ugly fix to not report IonicSelectable known exceptions which are not actual errors
            // (warning would have been better)
            if (hint.originalException['rejection'] === 'IonicSelectable is disabled or already opened.'
                || hint.originalException['rejection'] === 'IonicSelectable is disabled or already closed.') {
                // fix Circular dependency
                // eslint-disable-next-line no-console
                console.debug('[Sentry.beforeSend] Dropping event from IonicSelectable.', hint.originalException);
                return false;
            }

            // SAM-1087: Ugly fix to not report RxJS error when it times out
            if (hint.originalException['name'] === 'EmptyError' && hint.originalException['message'] === 'no elements in sequence') {
                // fix Circular dependency
                // eslint-disable-next-line no-console
                console.debug('[Sentry.beforeSend] Dropping event. Observable has timed out.', hint.originalException);
                return false;
            }

            if (isString(hint.originalException)) {
                // SMBP-24440: Do not report synthetic exception caught by preconfigured SentryErrorHandler when it looks like a server side error
                if (hint.originalException.match(/Server returned code [0-9]{3} with body/mi)) {
                    console.debug('[Sentry.beforeSend] Dropping event. Server side will not be reported:',
                        hint.originalException);
                    return false;
                }
            }
        }

        return true;
    }

    private isNonCapturable (event: Event): boolean {
        if (!!event
            && !!event.exception
            && !isEmpty(event.exception.values)
            && !!event.exception.values[0].value
        ) {
            const nonCapturableRegexp = new RegExp('Non-Error (?:promise rejection|exception) captured with keys');
            return nonCapturableRegexp.test(event.exception.values[0].value);
        } else {
            return false;
        }
    }
}
